import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Package, MapPin, Heart, User, LogOut, Trash2, Plus } from "lucide-react";
import OrderTimeline from "../components/orders/OrderTimeline";
import LoyaltySparkle from "../components/shared/LoyaltySparkle";

const STATUS_COLORS = {
  pending: "#FFD93D", paid: "#BAE1FF", packed: "#FFB347",
  shipped: "#DDC5FF", delivered: "#C8E6C9", cancelled: "#FFE4E4",
};

export default function Account() {
  const { user, token, loading, logout, api, refresh } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [newAddr, setNewAddr] = useState({ label: "Home", address: "", city: "Hyderabad", pincode: "" });
  const [profileForm, setProfileForm] = useState({ name: "", phone: "" });
  const [showLoyalty, setShowLoyalty] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    setProfileForm({ name: user.name, phone: user.phone || "" });
    // Auto-open loyalty modal once per unseen milestone
    if (user.loyalty_coupon && user.loyalty_coupon.code && !user.loyalty_coupon.seen) {
      setShowLoyalty(true);
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    if (tab === "orders") api().get("/auth/orders").then((r) => setOrders(r.data));
    if (tab === "wishlist") api().get("/auth/wishlist").then((r) => setWishlist(r.data));
  }, [tab, user]);

  if (loading || !user) return <div className="pt-32 pb-20 container-x">Loading…</div>;

  const addAddress = async (e) => {
    e.preventDefault();
    if (!newAddr.address || !newAddr.pincode) return toast.error("Fill all fields");
    await api().post("/auth/addresses", newAddr);
    setNewAddr({ label: "Home", address: "", city: "Hyderabad", pincode: "" });
    await refresh();
    toast.success("Address saved");
  };

  const removeAddr = async (id) => {
    await api().delete(`/auth/addresses/${id}`);
    await refresh();
    toast.success("Address removed");
  };

  const removeWish = async (id) => {
    await api().post(`/auth/wishlist/${id}`);
    setWishlist(wishlist.filter((p) => p.id !== id));
  };

  const updateProfile = async (e) => {
    e.preventDefault();
    await api().patch("/auth/me", profileForm);
    await refresh();
    toast.success("Profile updated");
  };

  const tabs = [
    { id: "orders", label: "Orders", icon: Package },
    { id: "addresses", label: "Addresses", icon: MapPin },
    { id: "wishlist", label: "Wishlist", icon: Heart },
    { id: "profile", label: "Profile", icon: User },
  ];

  return (
    <div className="pt-32 pb-20" data-testid="account-page">
      <div className="container-x">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="label-small">🎁 Your Account</p>
            <h1 className="font-doodle text-5xl text-[#2D2424] mt-2">Hi, {user.name}!</h1>
          </div>
          <button onClick={() => { logout(); navigate("/"); toast.success("See you soon!"); }} data-testid="logout-btn" className="btn-secondary">
            <LogOut className="w-4 h-4"/> Sign Out
          </button>
        </div>

        <div className="flex gap-2 mt-8 flex-wrap">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`account-tab-${t.id}`} className={`px-5 py-2 border-2 border-[#2D2424] rounded-full font-hand text-sm flex items-center gap-2 ${tab === t.id ? "bg-[#7A1F2B] text-white" : "bg-white text-[#2D2424]"}`} style={{ boxShadow: tab === t.id ? "3px 3px 0 #2D2424" : "2px 2px 0 #2D2424" }}>
                <Icon className="w-4 h-4"/> {t.label}
              </button>
            );
          })}
        </div>

        <div className="mt-8">
          {tab === "orders" && (
            <div className="space-y-3" data-testid="orders-list">
              {orders.length === 0 ? (
                <div className="doodle-card p-8 text-center bg-white">
                  <Package className="w-12 h-12 mx-auto text-[#7A1F2B]" strokeWidth={1.5}/>
                  <p className="font-doodle text-2xl mt-3">No orders yet</p>
                  <Link to="/shop/her" className="btn-primary mt-5 inline-flex">Start Shopping</Link>
                </div>
              ) : orders.map((o) => (
                <div key={o.id} className="doodle-card p-5 bg-white" data-testid={`order-${o.id}`}>
                  <div className="flex justify-between flex-wrap gap-3">
                    <div>
                      <p className="font-doodle text-2xl text-[#7A1F2B]">#{o.id.slice(0, 8)}</p>
                      <p className="text-xs font-hand text-[#5C5555]">{new Date(o.created_at).toLocaleString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-doodle text-2xl">₹{o.total.toLocaleString("en-IN")}</p>
                      <span className="doodle-pill text-xs uppercase" style={{ backgroundColor: STATUS_COLORS[o.status] || "#FFFAF3" }}>{o.status}</span>
                    </div>
                  </div>
                  <div className="mt-4 px-1">
                    <OrderTimeline status={o.status}/>
                  </div>
                  <div className="mt-3 text-sm font-hand text-[#5C5555]">
                    {o.items?.map((it) => `${it.name} × ${it.quantity}`).join(", ")}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "addresses" && (
            <div className="space-y-4" data-testid="addresses-list">
              <form onSubmit={addAddress} className="doodle-card p-5 bg-white grid grid-cols-1 sm:grid-cols-5 gap-3 items-end">
                <label className="flex flex-col"><span className="text-xs font-hand text-[#5C5555]">Label</span><input value={newAddr.label} onChange={(e) => setNewAddr({...newAddr, label: e.target.value})} className="border-2 border-[#2D2424] px-3 py-2 rounded-lg mt-1"/></label>
                <label className="flex flex-col sm:col-span-2"><span className="text-xs font-hand text-[#5C5555]">Address</span><input required value={newAddr.address} onChange={(e) => setNewAddr({...newAddr, address: e.target.value})} data-testid="address-input" className="border-2 border-[#2D2424] px-3 py-2 rounded-lg mt-1"/></label>
                <label className="flex flex-col"><span className="text-xs font-hand text-[#5C5555]">City</span><input value={newAddr.city} onChange={(e) => setNewAddr({...newAddr, city: e.target.value})} className="border-2 border-[#2D2424] px-3 py-2 rounded-lg mt-1"/></label>
                <label className="flex flex-col"><span className="text-xs font-hand text-[#5C5555]">Pincode</span><input required value={newAddr.pincode} onChange={(e) => setNewAddr({...newAddr, pincode: e.target.value})} data-testid="pincode-input" className="border-2 border-[#2D2424] px-3 py-2 rounded-lg mt-1"/></label>
                <button type="submit" className="btn-primary sm:col-span-5" data-testid="add-address-btn"><Plus className="w-4 h-4"/> Add Address</button>
              </form>
              {(user.addresses || []).map((a) => (
                <div key={a.id} className="doodle-card p-4 bg-white flex justify-between" data-testid={`address-${a.id}`}>
                  <div>
                    <p className="font-doodle text-xl">{a.label}</p>
                    <p className="text-sm font-hand text-[#5C5555]">{a.address}, {a.city} - {a.pincode}</p>
                  </div>
                  <button onClick={() => removeAddr(a.id)} className="text-[#7A1F2B]"><Trash2 className="w-4 h-4"/></button>
                </div>
              ))}
              {(user.addresses || []).length === 0 && <p className="font-hand text-[#5C5555] text-center py-8">No saved addresses yet — add one above 👆</p>}
            </div>
          )}

          {tab === "wishlist" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="wishlist-grid">
              {wishlist.length === 0 ? (
                <div className="doodle-card p-8 text-center bg-white sm:col-span-2 lg:col-span-3">
                  <Heart className="w-12 h-12 mx-auto text-[#FF6B9D]" strokeWidth={1.5}/>
                  <p className="font-doodle text-2xl mt-3">Your wishlist is empty</p>
                  <Link to="/shop/her" className="btn-primary mt-5 inline-flex">Browse Collection</Link>
                </div>
              ) : wishlist.map((p) => (
                <div key={p.id} className="doodle-card p-4 bg-white flex gap-3" data-testid={`wish-item-${p.id}`}>
                  <Link to={`/product/${p.slug}`} className="w-24 h-24 bg-[#F2E8E4] shrink-0"><img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover"/></Link>
                  <div className="flex-1">
                    <Link to={`/product/${p.slug}`} className="font-doodle text-xl hover:text-[#7A1F2B]">{p.name}</Link>
                    <p className="text-[#7A1F2B] font-doodle text-lg mt-1">₹{p.price.toLocaleString("en-IN")}</p>
                    <button onClick={() => removeWish(p.id)} className="mt-2 text-xs font-hand text-[#7A1F2B] flex items-center gap-1"><Trash2 className="w-3 h-3"/> Remove</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "profile" && (
            <form onSubmit={updateProfile} className="doodle-card p-6 bg-white max-w-lg space-y-4" data-testid="profile-form">
              <label className="block"><span className="text-xs font-hand text-[#5C5555]">Email (cannot change)</span><input disabled value={user.email} className="w-full border-2 border-[#E5DBD8] bg-gray-50 px-3 py-2 rounded-lg mt-1"/></label>
              <label className="block"><span className="text-xs font-hand text-[#5C5555]">Name</span><input value={profileForm.name} onChange={(e) => setProfileForm({...profileForm, name: e.target.value})} data-testid="profile-name" className="w-full border-2 border-[#2D2424] px-3 py-2 rounded-lg mt-1"/></label>
              <label className="block"><span className="text-xs font-hand text-[#5C5555]">Phone</span><input value={profileForm.phone} onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})} data-testid="profile-phone" className="w-full border-2 border-[#2D2424] px-3 py-2 rounded-lg mt-1"/></label>
              <button type="submit" className="btn-primary" data-testid="profile-save">Save Changes</button>
            </form>
          )}
        </div>
      </div>
      {showLoyalty && user?.loyalty_coupon && (
        <LoyaltySparkle
          userName={user.name || ""}
          couponCode={user.loyalty_coupon.code}
          discountPercent={user.loyalty_coupon.discount_percent || 10}
          minOrder={user.loyalty_coupon.min_order || 2000}
          token={token}
          onClose={() => { setShowLoyalty(false); refresh(); }}
        />
      )}
    </div>
  );
}
